import {
  companionSseControllerTicket,
  CompanionSseEvent,
  getCompanionSseControllerConnectUrl,
  type CompanionSseBarcodePayloadDto,
  type CompanionSseConnectedPayloadDto,
  type CompanionSseFilePayloadDto,
  type CompanionSseOfferPayloadDto,
  type CompanionSseOfferSourcePayloadDto,
  type CompanionSsePresencePayloadDto,
} from '@/api';
import { defineStore } from 'pinia';
import { computed, onUnmounted, reactive, unref, watch, type MaybeRef } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';

const { VITE_API_URL = '/api' } = import.meta.env;

/** Ceiling for the reconnect backoff. */
const MAX_RETRY_DELAY = 30000;
/** Give up after this many consecutive failed attempts; `reconnect()` resets it. */
const MAX_ATTEMPTS = 8;

export type CompanionSseStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  /** Session expired or was deleted - every further request 404s, so we stop. */
  | 'gone'
  | 'failed';

export interface CompanionSseHandlers {
  onConnected?: (payload: CompanionSseConnectedPayloadDto) => void;
  /** A desktop client opened or dropped its stream on the session. */
  onPresenceChanged?: (payload: CompanionSsePresencePayloadDto) => void;
  onFileCreated?: (payload: CompanionSseFilePayloadDto) => void;
  onFileRemoved?: (payload: CompanionSseFilePayloadDto) => void;
  onBarcodeScanned?: (payload: CompanionSseBarcodePayloadDto) => void;
  onOfferSourceChanged?: (payload: CompanionSseOfferSourcePayloadDto) => void;
  onOfferCreated?: (payload: CompanionSseOfferPayloadDto) => void;
  onGone?: (companionId: string) => void;
}

interface CompanionSseConnection {
  source: EventSource | null;
  subscribers: Set<CompanionSseHandlers>;
  attempts: number;
  retryTimer: number | null;
  isStarting: boolean;
}

const parsePayload = <T>(event: MessageEvent<string>): T | null => {
  try {
    return JSON.parse(event.data) as T;
  } catch (error) {
    console.error('Failed to parse companion SSE payload', { error, data: event.data });
    return null;
  }
};

/** The SSE `event:` line carries the name, so every frame arrives on its own listener. */
const listenForEvent = <T>(
  source: EventSource,
  event: CompanionSseEvent,
  run: (payload: T) => void,
) =>
  source.addEventListener(event, (message) => {
    const payload = parsePayload<T>(message as MessageEvent<string>);
    if (payload) run(payload);
  });

const getStreamUrl = (companionId: string, ticket: string) => {
  const base = /^https?:\/\//.test(VITE_API_URL)
    ? VITE_API_URL
    : `${window.location.origin}${VITE_API_URL}`;
  const url = new URL(`${base}${getCompanionSseControllerConnectUrl(companionId)}`);
  url.searchParams.set('ticket', ticket);

  return url.toString();
};

export const useCompanionSseStore = defineStore('companion-sse', () => {
  const sseStatus = reactive(new Map<string, CompanionSseStatus>());
  // Connection bookkeeping stays out of the reactive graph: only the status is
  // ever rendered, and an EventSource behind a proxy is asking for trouble.
  const connections = new Map<string, CompanionSseConnection>();

  const getConnectionStatus = (companionId: string): CompanionSseStatus =>
    sseStatus.get(companionId) ?? 'idle';

  const notify = (companionId: string, run: (handlers: CompanionSseHandlers) => void) => {
    const connection = connections.get(companionId);
    if (!connection) return;

    for (const handlers of [...connection.subscribers]) run(handlers);
  };

  const teardown = (companionId: string, status: CompanionSseStatus) => {
    const connection = connections.get(companionId);
    if (!connection) return;

    if (connection.retryTimer) clearTimeout(connection.retryTimer);
    connection.retryTimer = null;
    connection.source?.close();
    connection.source = null;

    sseStatus.set(companionId, status);
  };

  const scheduleReconnect = (companionId: string) => {
    const connection = connections.get(companionId);
    if (!connection || connection.subscribers.size === 0) return;

    if (connection.attempts >= MAX_ATTEMPTS) {
      teardown(companionId, 'failed');
      return;
    }

    // Jittered exponential backoff - every attempt costs a fresh ticket, so
    // hammering the ticket route on a dead session helps nobody.
    const backoff = Math.min(MAX_RETRY_DELAY, 1000 * 2 ** connection.attempts);
    const delay = backoff * (0.5 + Math.random());
    connection.attempts += 1;
    sseStatus.set(companionId, 'reconnecting');

    connection.retryTimer = window.setTimeout(() => {
      connection.retryTimer = null;
      void openStream(companionId);
    }, delay);
  };

  const openStream = async (companionId: string) => {
    const connection = connections.get(companionId);
    if (!connection || connection.isStarting || connection.source) return;
    if (connection.subscribers.size === 0) return;

    connection.isStarting = true;

    try {
      if (getConnectionStatus(companionId) !== 'reconnecting')
        sseStatus.set(companionId, 'connecting');

      const { status, data } = await companionSseControllerTicket(companionId);

      // A 404 means the session expired or was deleted. The file list and every
      // later ticket answer the same way, so this is terminal, not transient.
      if (status === 404) {
        teardown(companionId, 'gone');
        notify(companionId, (handlers) => handlers.onGone?.(companionId));
        return;
      }

      if (status !== 200) {
        scheduleReconnect(companionId);
        return;
      }

      // The last subscriber may have left while the ticket was in flight.
      if (connection.subscribers.size === 0) return;

      const source = new EventSource(getStreamUrl(companionId, data.ticket));
      connection.source = source;

      listenForEvent<CompanionSseConnectedPayloadDto>(
        source,
        CompanionSseEvent.CONNECTED,
        (payload) => {
          connection.attempts = 0;
          sseStatus.set(companionId, 'open');
          notify(companionId, (handlers) => handlers.onConnected?.(payload));
        },
      );

      listenForEvent<CompanionSsePresencePayloadDto>(
        source,
        CompanionSseEvent.PRESENCE_CHANGED,
        (payload) => notify(companionId, (handlers) => handlers.onPresenceChanged?.(payload)),
      );

      listenForEvent<CompanionSseFilePayloadDto>(
        source,
        CompanionSseEvent.FILE_CREATED,
        (payload) => notify(companionId, (handlers) => handlers.onFileCreated?.(payload)),
      );

      listenForEvent<CompanionSseFilePayloadDto>(
        source,
        CompanionSseEvent.FILE_REMOVED,
        (payload) => notify(companionId, (handlers) => handlers.onFileRemoved?.(payload)),
      );

      listenForEvent<CompanionSseBarcodePayloadDto>(
        source,
        CompanionSseEvent.BARCODE_SCANNED,
        (payload) => notify(companionId, (handlers) => handlers.onBarcodeScanned?.(payload)),
      );

      listenForEvent<CompanionSseOfferSourcePayloadDto>(
        source,
        CompanionSseEvent.OFFER_SOURCE_CHANGED,
        (payload) => notify(companionId, (handlers) => handlers.onOfferSourceChanged?.(payload)),
      );

      listenForEvent<CompanionSseOfferPayloadDto>(
        source,
        CompanionSseEvent.OFFER_CREATED,
        (payload) => notify(companionId, (handlers) => handlers.onOfferCreated?.(payload)),
      );

      // `heartbeat` is deliberately unhandled - it only holds the connection open.

      // The ticket is consumed on connect, so the native auto-reconnect would
      // replay a dead ticket into a 401 loop. Close and start over with a fresh
      // one instead; whether the session is merely unreachable or gone for good
      // is what the next ticket request tells us.
      source.onerror = () => {
        if (connection.source !== source) return;

        connection.source = null;
        source.close();
        scheduleReconnect(companionId);
      };
    } catch (error) {
      console.error('Failed to open companion SSE stream', { error, companionId });
      scheduleReconnect(companionId);
    } finally {
      connection.isStarting = false;
    }
  };

  const subscribe = (companionId: string, handlers: CompanionSseHandlers) => {
    if (!companionId) return;

    const connection = connections.get(companionId) ?? {
      source: null,
      subscribers: new Set<CompanionSseHandlers>(),
      attempts: 0,
      retryTimer: null,
      isStarting: false,
    };
    connections.set(companionId, connection);
    connection.subscribers.add(handlers);

    // One stream per session, shared by every subscriber - the server caps how
    // many a single session may hold open.
    const isIdle = !connection.source && !connection.isStarting && !connection.retryTimer;
    if (isIdle && getConnectionStatus(companionId) !== 'gone') void openStream(companionId);
  };

  const unsubscribe = (companionId: string, handlers: CompanionSseHandlers) => {
    const connection = connections.get(companionId);
    if (!connection) return;

    connection.subscribers.delete(handlers);
    if (connection.subscribers.size > 0) return;

    teardown(companionId, 'idle');
    connections.delete(companionId);
    sseStatus.delete(companionId);
  };

  /** Manual retry after the backoff gave up, or after a session was recreated. */
  const reconnect = (companionId: string) => {
    const connection = connections.get(companionId);
    if (!connection) return;

    teardown(companionId, 'idle');
    connection.attempts = 0;
    void openStream(companionId);
  };

  return {
    sseStatus,
    getConnectionStatus,
    subscribe,
    unsubscribe,
    reconnect,
  };
});

export interface UseCompanionSseOptions extends CompanionSseHandlers {
  /** Skip the component lifecycle - for app-lifetime subscriptions. */
  global?: boolean;
  /** While false the stream stays closed, e.g. a dialog that is not open. */
  enabled?: MaybeRef<boolean>;
}

export const useCompanionSse = (
  companionId: MaybeRef<string>,
  { global: isGlobal, enabled = true, ...handlers }: UseCompanionSseOptions = {},
) => {
  const store = usePiniaRefStore(useCompanionSseStore());

  let subscribedId = '';

  const detach = () => {
    if (subscribedId) store.unsubscribe(subscribedId, handlers);
    subscribedId = '';
  };

  const attach = () => {
    const wantedId = unref(enabled) ? unref(companionId) : '';
    if (wantedId === subscribedId) return;

    detach();
    if (!wantedId) return;

    store.subscribe(wantedId, handlers);
    subscribedId = wantedId;
  };

  const stopWatching = watch(() => [unref(companionId), unref(enabled)], attach, {
    immediate: true,
  });

  if (!isGlobal)
    onUnmounted(() => {
      stopWatching();
      detach();
    });

  const connectionStatus = computed(() => store.getConnectionStatus(unref(companionId)));

  return {
    ...store,
    connectionStatus,
    isLive: computed(() => connectionStatus.value === 'open'),
    reconnectCompanionSse: () => store.reconnect(unref(companionId)),
  };
};
